import {
  ClipboardList, Video, ListChecks, FileText, HelpCircle, CheckCircle2, ArrowRight,
} from "lucide-react";
import { Eyebrow, SectionTitle, SectionSub, GhostButton } from "@/components/web/atoms";
import { Reveal, trackHomeEvent, scrollToId } from "@/components/web/home/primitives";

/* Section 9 — Meeting intelligence: Before (Prepare Me) → During (Meet) →
   After (Execute). */
const STAGES = [
  {
    key: "before", stage: "Before", title: "Prepare Me", tone: "ai", icon: ClipboardList,
    lead: "TeamNest builds a brief from authorized context:",
    items: ["Previous chat", "Related documents", "Open tasks", "Prior meeting notes", "Saved decisions"],
  },
  {
    key: "during", stage: "During", title: "Meet", tone: "brand", icon: Video,
    lead: "Capture the conversation as it happens:",
    items: ["Audio / video", "AI transcript", "Highlights", "Decisions", "Action items"],
  },
  {
    key: "after", stage: "After", title: "Execute", tone: "brand", icon: ListChecks,
    lead: "Turn the meeting into work:",
    items: ["Meeting recap", "Tasks & follow-ups", "Decisions", "Saved knowledge"],
  },
];

function BriefCard() {
  const rows = [
    { icon: HelpCircle, label: "Purpose", v: "Align on Q3 launch scope" },
    { icon: FileText, label: "Relevant documents", v: "3 attached" },
    { icon: CheckCircle2, label: "Outstanding tasks", v: "4 open" },
    { icon: HelpCircle, label: "Questions to ask", v: "5 prepared" },
  ];
  return (
    <div className="rounded-[16px] border border-[var(--w-ai)]/30 bg-[var(--w-surface)] p-5">
      <div className="flex items-center gap-1.5 mb-3">
        <ClipboardList className="w-3.5 h-3.5 text-[var(--w-ai)]" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--w-ai)]">Meeting brief</span>
      </div>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-2.5">
            <span className="w-7 h-7 rounded-lg bg-[var(--w-ai-tint)] flex items-center justify-center shrink-0">
              <r.icon className="w-3.5 h-3.5 text-[var(--w-ai)]" />
            </span>
            <span className="text-[12.5px] text-[var(--w-text-dim)] flex-1">{r.label}</span>
            <span className="text-[12.5px] font-medium text-[var(--w-text)]">{r.v}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {["Ask AI", "Share Brief", "Create Agenda"].map((b) => (
          <span key={b} className="inline-flex items-center h-8 px-3 rounded-lg border border-[var(--w-hairline)] bg-[var(--w-bg2)] text-[12px] font-medium text-[var(--w-text-dim)]">{b}</span>
        ))}
      </div>
    </div>
  );
}

export default function MeetingIntelligenceSection() {
  return (
    <section
      id="meeting-intelligence"
      className="relative py-20 md:py-28 px-5 border-t border-[var(--w-hairline)] bg-[var(--w-bg2)] scroll-mt-20"
      data-testid="home-meeting-intelligence"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal className="max-w-3xl">
          <Eyebrow tone="ai" className="mb-4">Meeting intelligence</Eyebrow>
          <SectionTitle>Meetings that actually become work.</SectionTitle>
          <SectionSub className="mt-5">
            Prepare before the meeting, capture the conversation, and turn decisions into work afterward.
          </SectionSub>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {STAGES.map((s, i) => {
            const accent = s.tone === "ai" ? "var(--w-ai)" : "var(--w-brand)";
            const tint = s.tone === "ai" ? "var(--w-ai-tint)" : "var(--w-brand-tint)";
            return (
              <Reveal key={s.key} delay={i * 80}>
                <div className="h-full rounded-[18px] border border-[var(--w-hairline)] bg-[var(--w-surface)] p-5" data-testid={`meeting-stage-${s.key}`}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--w-text-mute)] mb-3">{s.stage}</div>
                  <div className="flex items-center gap-2.5 mb-3">
                    <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: tint }}>
                      <s.icon className="w-5 h-5" style={{ color: accent }} />
                    </span>
                    <span className="text-[18px] font-bold text-[var(--w-text)]">{s.title}</span>
                  </div>
                  <p className="text-[13px] text-[var(--w-text-dim)] mb-3">{s.lead}</p>
                  <ul className="space-y-1.5">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-center gap-2 text-[13px] text-[var(--w-text-dim)]">
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[1fr_0.9fr] gap-8 items-center">
          <Reveal>
            <BriefCard />
          </Reveal>
          <Reveal delay={120}>
            <p className="text-[22px] md:text-[26px] font-bold text-[var(--w-text)] leading-tight" style={{ textWrap: "balance" }}>
              The meeting doesn't disappear when everyone hangs up.
            </p>
            <p className="mt-4 text-[15px] leading-7 text-[var(--w-text-dim)]">
              Every meeting becomes searchable team memory — the recap, decisions and tasks all live in the
              conversation where the work happens.
            </p>
            <div className="mt-6">
              <GhostButton
                as="button"
                type="button"
                data-testid="meeting-intelligence-cta"
                onClick={() => { trackHomeEvent("meeting-intelligence-cta", { source: "meeting" }); scrollToId("memory"); }}
              >
                Explore Meetings <ArrowRight className="w-4 h-4" />
              </GhostButton>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
